import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import {
  InvoiceRecord,
  analyzeDepositDetails,
  depositInvoiceAmountBase,
  depositAdjustmentLineAmountBase,
  depositAdjustmentLineAmountUsd,
  formatInvoiceDateShort,
  buildDepositInfoCsv,
  downloadTextFile,
} from '../../../../utils/invoiceUtils';
import { ClientDetailData } from '../../../../hooks/useClientDetail';

/* ─── Props ──────────────────────────────────────────────────────────────── */

interface DepositInfoTabProps {
  data: ClientDetailData;
}

/* ─── Helpers ────────────────────────────────────────────────────────────── */

const fmt = (n: number, ccy: string): string => {
  if (ccy === 'USD') return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${n.toLocaleString('en-US', { maximumFractionDigits: 0 })} ${ccy}`;
};

const fmtUsd = (n: number): string =>
  `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/* ─── Component ──────────────────────────────────────────────────────────── */

export const DepositInfoTab: React.FC<DepositInfoTabProps> = ({ data }) => {
  const invoiceRecordsRaw: InvoiceRecord[] = data?.detailed_invoices?.invoice_records || [];
  const baseCurrency =
    data?.metadata?.original_currency ||
    data?.currency_analysis?.primary_currency ||
    'USD';
  const clientName = data?.contact_profile?.basic_info?.contact_name || '';
  const contactId = data?.contact_profile?.basic_info?.contact_id;

  const { metrics, paidDepositInvoices, unpaidDepositSubjectInvoices, adjustmentRows } = useMemo(
    () => analyzeDepositDetails(invoiceRecordsRaw),
    [invoiceRecordsRaw]
  );

  const [expandedPaid, setExpandedPaid] = useState<Set<string | number>>(new Set());
  const [expandedUnpaid, setExpandedUnpaid] = useState<Set<string | number>>(new Set());

  const togglePaid = (id: string | number) => {
    setExpandedPaid((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleUnpaid = (id: string | number) => {
    setExpandedUnpaid((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exportCsv = () => {
    const csv = buildDepositInfoCsv({
      clientName,
      contactId,
      baseCurrency,
      metrics,
      paidDepositInvoices,
      unpaidDepositSubjectInvoices,
      adjustmentRows,
    });
    const fileBase = `${clientName || 'client'}_deposit_info`.replace(/\s+/g, '_');
    downloadTextFile(`${fileBase}.csv`, csv, 'text/csv;charset=utf-8;');
  };

  const hasAnyDeposits =
    paidDepositInvoices.length > 0 ||
    unpaidDepositSubjectInvoices.length > 0 ||
    adjustmentRows.length > 0;

  return (
    <div className="space-y-3">
      {/* ── Section A: Summary strip ──────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-slate-100">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
            Deposit Summary
          </span>
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-slate-600">not filtered · all deposits</span>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 hover:border-slate-300 transition-colors"
            >
              <Download size={12} /> Export CSV
            </button>
          </div>
        </div>
        <div className="flex divide-x divide-slate-100">
          {[
            { label: 'Total Deposit', base: metrics.depositBase, usd: metrics.depositUsd, accent: 'bg-violet-400' },
            { label: 'Deposit Adjustment', base: metrics.depositAdjustmentBase, usd: metrics.depositAdjustmentUsd, accent: 'bg-orange-400' },
            {
              label: 'Net Deposit Balance',
              base: metrics.netDepositBase,
              usd: metrics.netDepositUsd,
              accent: metrics.netDepositBase >= 0 ? 'bg-emerald-500' : 'bg-red-500',
            },
          ].map(({ label, base, usd, accent }) => (
            <div key={label} className="flex items-stretch flex-1">
              <div className={`w-0.5 flex-none ${accent}`} />
              <div className="flex-1 px-4 py-3">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-1">{label}</p>
                <p className="text-[15px] font-bold text-[#0D0D0D] tabular-nums leading-none">
                  {baseCurrency === 'USD' ? fmtUsd(usd) : fmt(base, baseCurrency)}
                </p>
                {baseCurrency !== 'USD' && Math.abs(base - usd) > 0.01 && (
                  <p className="text-[11px] text-slate-600 tabular-nums mt-0.5">{fmtUsd(usd)}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!hasAnyDeposits && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md p-12 text-center">
          <p className="text-slate-600 text-sm">No deposit records found for this client.</p>
        </div>
      )}

      {/* ── Section B: Paid Deposit Invoices ─────────────────────── */}
      {paidDepositInvoices.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
              Paid Deposit Invoices
            </span>
            <span className="text-[10px] bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 font-semibold px-2 py-0.5 rounded-full">
              {paidDepositInvoices.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice No.</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[160px]">Subject</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount ({baseCurrency})</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount (USD)</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Invoice Date</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Due Date</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Paid Date</th>
                </tr>
              </thead>
              <tbody>
                {paidDepositInvoices.map((inv, idx) => {
                  const id = inv.invoice_id ?? idx;
                  const expanded = expandedPaid.has(id);
                  const amtBase = depositInvoiceAmountBase(inv);
                  const amtUsd = parseFloat(String(inv.amount_usd)) || 0;
                  const hasLines = (inv.line_items || []).length > 0;

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'} ${hasLines ? 'cursor-pointer hover:bg-blue-50/60' : ''} transition-colors`}
                        onClick={() => hasLines && togglePaid(id)}
                      >
                        <td className="px-3 py-2.5 font-medium text-slate-800 text-xs whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            {hasLines && (expanded ? <ChevronDown size={12} className="text-slate-600" /> : <ChevronRight size={12} className="text-slate-600" />)}
                            {inv.invoice_number || `INV-${inv.invoice_id}`}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700 text-xs max-w-[200px] truncate">{inv.subject || '—'}</td>
                        <td className="px-3 py-2.5 text-right text-emerald-700 tabular-nums font-semibold text-xs">{fmt(amtBase, baseCurrency)}</td>
                        <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums text-xs">{fmtUsd(amtUsd)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatInvoiceDateShort(inv.invoice_date)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatInvoiceDateShort(inv.due_date)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatInvoiceDateShort(inv.paid_date)}</td>
                      </tr>
                      {expanded && (inv.line_items || []).length > 0 && (
                        <tr>
                          <td colSpan={7} className="bg-slate-50 border-t border-slate-100 px-6 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-600 border-b border-slate-200">
                                  <th className="text-left py-1 font-semibold">Description</th>
                                  <th className="text-right py-1 font-semibold">Qty</th>
                                  <th className="text-right py-1 font-semibold">Unit Price</th>
                                  <th className="text-right py-1 font-semibold">Tax %</th>
                                  <th className="text-right py-1 font-semibold">Line Total</th>
                                  <th className="text-right py-1 font-semibold">Line Total (USD)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(inv.line_items || []).map((line, li) => (
                                  <tr key={li} className="border-t border-slate-100">
                                    <td className="py-1.5 text-slate-700 pr-3">{line.description || '—'}</td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">{line.quantity ?? '—'}</td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">{line.unit_price ?? '—'}</td>
                                    <td className="py-1.5 text-right text-slate-500 tabular-nums">{line.tax_percentage ?? '—'}</td>
                                    <td className="py-1.5 text-right text-slate-700 tabular-nums font-medium">{line.line_total ?? '—'}</td>
                                    <td className="py-1.5 text-right text-slate-600 tabular-nums">{line.line_total_usd ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section C: Unpaid Deposit Subject Invoices ────────────── */}
      {unpaidDepositSubjectInvoices.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
              Unpaid Deposit Subject Invoices
            </span>
            <span className="text-[10px] bg-amber-50 text-amber-700 ring-1 ring-amber-200 font-semibold px-2 py-0.5 rounded-full">
              {unpaidDepositSubjectInvoices.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Invoice No.</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[160px]">Subject</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount ({baseCurrency})</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount (USD)</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Invoice Date</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Due Date</th>
                </tr>
              </thead>
              <tbody>
                {unpaidDepositSubjectInvoices.map((inv, idx) => {
                  const id = inv.invoice_id ?? idx;
                  const expanded = expandedUnpaid.has(id);
                  const amtBase = depositInvoiceAmountBase(inv);
                  const amtUsd = parseFloat(String(inv.amount_usd)) || 0;
                  const hasLines = (inv.line_items || []).length > 0;

                  return (
                    <React.Fragment key={id}>
                      <tr
                        className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'} ${hasLines ? 'cursor-pointer hover:bg-blue-50/60' : ''} transition-colors`}
                        onClick={() => hasLines && toggleUnpaid(id)}
                      >
                        <td className="px-3 py-2.5 font-medium text-slate-800 text-xs whitespace-nowrap">
                          <span className="flex items-center gap-1">
                            {hasLines && (expanded ? <ChevronDown size={12} className="text-slate-600" /> : <ChevronRight size={12} className="text-slate-600" />)}
                            {inv.invoice_number || `INV-${inv.invoice_id}`}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-slate-700 text-xs max-w-[200px] truncate">{inv.subject || '—'}</td>
                        <td className="px-3 py-2.5 text-xs">
                          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                            {inv.status || '—'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-right text-slate-700 tabular-nums font-medium text-xs">{fmt(amtBase, baseCurrency)}</td>
                        <td className="px-3 py-2.5 text-right text-slate-500 tabular-nums text-xs">{fmtUsd(amtUsd)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatInvoiceDateShort(inv.invoice_date)}</td>
                        <td className="px-3 py-2.5 text-slate-500 text-xs whitespace-nowrap">{formatInvoiceDateShort(inv.due_date)}</td>
                      </tr>
                      {expanded && (inv.line_items || []).length > 0 && (
                        <tr>
                          <td colSpan={7} className="bg-slate-50 border-t border-slate-100 px-6 py-3">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-slate-600 border-b border-slate-200">
                                  <th className="text-left py-1 font-semibold">Description</th>
                                  <th className="text-right py-1 font-semibold">Line Total</th>
                                  <th className="text-right py-1 font-semibold">Line Total (USD)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(inv.line_items || []).map((line, li) => (
                                  <tr key={li} className="border-t border-slate-100">
                                    <td className="py-1.5 text-slate-700 pr-3">{line.description || '—'}</td>
                                    <td className="py-1.5 text-right text-slate-700 tabular-nums font-medium">{line.line_total ?? '—'}</td>
                                    <td className="py-1.5 text-right text-slate-600 tabular-nums">{line.line_total_usd ?? '—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Section D: Deposit Adjustment Line Items ──────────────── */}
      {adjustmentRows.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600">
              Deposit Adjustment Line Items
            </span>
            <span className="text-[10px] bg-slate-100 text-slate-600 font-semibold px-2 py-0.5 rounded-full">
              {adjustmentRows.length}
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b-2 border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Parent Invoice</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[140px]">Parent Subject</th>
                  <th className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[160px]">Line Description</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Line Total ({baseCurrency})</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Line Total (USD)</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Adj Amt ({baseCurrency})</th>
                  <th className="px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">Adj Amt (USD)</th>
                </tr>
              </thead>
              <tbody>
                {adjustmentRows.map(({ invoice: inv, line, lineIndex }, idx) => {
                  const adjBase = depositAdjustmentLineAmountBase(line);
                  const adjUsd = depositAdjustmentLineAmountUsd(line);
                  return (
                    <tr
                      key={`${inv.invoice_id}-${lineIndex}`}
                      className={`border-t border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/70' : 'bg-white'} hover:bg-blue-50/60 transition-colors`}
                    >
                      <td className="px-3 py-2.5 font-medium text-slate-800 text-xs whitespace-nowrap">
                        {inv.invoice_number || `INV-${inv.invoice_id}`}
                      </td>
                      <td className="px-3 py-2.5 text-slate-700 text-xs max-w-[160px] truncate">{inv.subject || '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600 text-xs max-w-[200px] truncate">{line.description || '—'}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums text-xs">{line.line_total ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right text-slate-600 tabular-nums text-xs">{line.line_total_usd ?? '—'}</td>
                      <td className="px-3 py-2.5 text-right text-red-600 tabular-nums font-semibold text-xs">{fmt(adjBase, baseCurrency)}</td>
                      <td className="px-3 py-2.5 text-right text-red-400 tabular-nums text-xs">{fmtUsd(adjUsd)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepositInfoTab;
